import { ReactNode, useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FilterBar } from "~/components/Filter/FilterBar/FilterBar";
import { FilterForm } from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import { QuickFilterButton } from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ActiveFilterButton } from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { groupBy, upperFirst } from "lodash-es";
import {
  AggregateScoreState,
  GetAggregateScoresQueryVariables,
  ScoreRating,
} from "~/operations";
export type PolicyChecksFilterVariables = Pick<
  NonNullable<GetAggregateScoresQueryVariables["filter"]>,
  "rating" | "state" | "queryTerms"
>;

export type UsePolicyChecksFilterContextOutput = {
  filterVariables: PolicyChecksFilterVariables;
  filterForm: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

const SEARCH_PARAMS = {
  RISK: "risk",
  STATE: "state",
  QUERY_TERM: "queryTerm",
} as const;

// Constants for filter options
const RISK_RATING_OPTIONS = [
  { label: "Critical", value: ScoreRating.Critical },
  { label: "High", value: ScoreRating.High },
  { label: "Medium", value: ScoreRating.Medium },
  { label: "Low", value: ScoreRating.Low },
];

const STATUS_OPTIONS = [
  { label: "Scored", value: AggregateScoreState.Enabled },
  { label: "Preview", value: AggregateScoreState.Preview },
];

// Helper to reset pagination URL params when filters change
function resetPaginationParams(url: URL, prefix = "checks"): URL {
  url.searchParams.delete(`${prefix}.after`);
  url.searchParams.delete(`${prefix}.page`);
  return url;
}

export function usePolicyChecksFilterContext(): UsePolicyChecksFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  // Parse filters from URL params
  const riskRatingFilters = useMemo(() => {
    const params = searchParams.getAll(SEARCH_PARAMS.RISK);
    return params.length
      ? params.filter((r): r is ScoreRating =>
          Object.values(ScoreRating).includes(r as ScoreRating),
        )
      : undefined;
  }, [searchParams]);

  const statusFilter = useMemo(() => {
    const param = searchParams.get(SEARCH_PARAMS.STATE);
    return param &&
      Object.values(AggregateScoreState).includes(param as AggregateScoreState)
      ? (param as AggregateScoreState)
      : undefined;
  }, [searchParams]);

  const searchFilters = useMemo(() => {
    const params = searchParams.getAll(SEARCH_PARAMS.QUERY_TERM);
    return params.length ? params : undefined;
  }, [searchParams]);

  // Filter management callbacks
  const addFilter = useCallback(
    (param: string, value: string, multiple = false) => {
      const url = resetPaginationParams(new URL(location.href));
      if (multiple) {
        const existing = url.searchParams.getAll(param);
        if (!existing.includes(value)) url.searchParams.append(param, value);
      } else {
        url.searchParams.set(param, value);
      }
      navigate({ pathname: url.pathname, search: url.search });
    },
    [navigate],
  );

  const removeFilter = useCallback(
    (param: string, value?: string) => {
      const url = resetPaginationParams(new URL(location.href));
      if (value == null) {
        url.searchParams.delete(param);
      } else {
        const kept = url.searchParams.getAll(param).filter((v) => v !== value);
        url.searchParams.delete(param);
        kept.forEach((v) => url.searchParams.append(param, v));
      }
      navigate({ pathname: url.pathname, search: url.search });
    },
    [navigate],
  );

  const resetFilters = useCallback(() => {
    const url = resetPaginationParams(new URL(location.href));
    Object.values(SEARCH_PARAMS).forEach((p) => url.searchParams.delete(p));
    navigate({ pathname: url.pathname, search: url.search });
  }, [navigate]);

  const activeFilters = useMemo(() => {
    return [
      ...(riskRatingFilters?.map((rating) => ({
        label: "Risk",
        value: upperFirst(rating.toLowerCase()),
        onClick: () => removeFilter(SEARCH_PARAMS.RISK, rating),
      })) || []),
      // TODO: Enable when backend is ready
      // ...(statusFilter
      //   ? [
      //       {
      //         label: "Status",
      //         value: statusFilter
      //           .toLowerCase()
      //           .split("_")
      //           .map((s) => upperFirst(s))
      //           .join(" "),
      //         onClick: () => removeFilter(SEARCH_PARAMS.STATE),
      //       },
      //     ]
      //   : []),
      ...(searchFilters?.map((search) => ({
        label: "Search",
        value: search,
        onClick: () => removeFilter(SEARCH_PARAMS.QUERY_TERM, search),
      })) || []),
    ];
  }, [riskRatingFilters, statusFilter, searchFilters, removeFilter]);

  const fieldSuggestions = useMemo(() => {
    if (focusedField) {
      switch (focusedField.field) {
        case SEARCH_PARAMS.RISK:
          return RISK_RATING_OPTIONS.map((option) => ({
            field: SEARCH_PARAMS.RISK,
            label: option.label,
            value: option.value,
            param: SEARCH_PARAMS.RISK,
            isMultiple: true,
          }));
        // case SEARCH_PARAMS.STATE:
        //   return STATUS_OPTIONS.map((option) => ({
        //     field: SEARCH_PARAMS.STATE,
        //     label: option.label,
        //     value: option.value,
        //     param: SEARCH_PARAMS.STATE,
        //     isMultiple: false,
        //   }));
        default:
          return [];
      }
    }

    const suggestions: FilterMenuOption[] = [
      ...(searchInput.length > 0
        ? [
            {
              field: SEARCH_PARAMS.QUERY_TERM,
              label: `Press enter to free text search "${searchInput}"`,
              value: SEARCH_PARAMS.QUERY_TERM,
              isDisabled: true,
            },
          ]
        : []),
      {
        field: SEARCH_PARAMS.RISK,
        label: "Risk",
        value: SEARCH_PARAMS.RISK,
      },
      // {
      //   field: SEARCH_PARAMS.STATE,
      //   label: "Status",
      //   value: SEARCH_PARAMS.STATE,
      // },
    ];

    return suggestions.filter((s) =>
      s.label.toLowerCase().includes(searchInput.toLowerCase()),
    );
  }, [searchInput, focusedField]);

  // Render FilterBar
  const filterBar = (
    <FilterBar
      fieldOptions={fieldSuggestions}
      focusedField={focusedField}
      inputValue={searchInput}
      onInputValueChange={setSearchInput}
      onFocusedRemove={() => setFocusedField(undefined)}
      onFilterSelect={(filter) => {
        setSearchInput("");
        if (!focusedField) {
          setFocusedField(filter);
        } else {
          setFocusedField(undefined);
          if (filter.param) {
            addFilter(filter.param, filter.value, filter.isMultiple);
          }
        }
      }}
      onFilterCreate={(filter) => {
        setFocusedField(undefined);
        setSearchInput("");
        addFilter(SEARCH_PARAMS.QUERY_TERM, filter, true);
      }}
    />
  );

  // Render QuickFilterBar
  const quickFilterBar = (
    <QuickFilterBar>
      <QuickFilterButton
        label="Risk"
        activeCount={riskRatingFilters?.length || 0}
        menuOptions={RISK_RATING_OPTIONS.map((o) => ({
          ...o,
          field: SEARCH_PARAMS.RISK,
          param: SEARCH_PARAMS.RISK,
          isMultiple: true,
          onSelect: () => addFilter(SEARCH_PARAMS.RISK, o.value, true),
        }))}
      />
      {/* TODO: Enable when backend is ready
      <QuickFilterButton
        label="Status"
        activeCount={statusFilter ? 1 : 0}
        menuOptions={STATUS_OPTIONS.map((o) => ({
          ...o,
          field: SEARCH_PARAMS.STATE,
          param: SEARCH_PARAMS.STATE,
          onSelect: () => addFilter(SEARCH_PARAMS.STATE, o.value, false),
        }))}
      />
      */}
    </QuickFilterBar>
  );

  // Render ActiveFilterBar
  const activeFilterBar =
    activeFilters.length > 0 ? (
      <ActiveFilterBar onResetClick={resetFilters}>
        {Object.entries(groupBy(activeFilters, "label")).flatMap(
          ([label, values], index) => {
            const buttons = values.map((props) => (
              <ActiveFilterButton key={props.label + props.value} {...props} />
            ));
            const separator =
              index === 0 ? null : (
                <ChevronRightIcon
                  key={label + "-separator"}
                  sx={{ fontSize: "16px", color: "text.secondary" }}
                />
              );
            return separator ? [separator, ...buttons] : buttons;
          },
        )}
      </ActiveFilterBar>
    ) : null;

  // Combine all filter UI elements
  const filterForm = (
    <FilterForm
      filterBar={filterBar}
      quickFilterBar={quickFilterBar}
      activeFilterBar={activeFilterBar}
    />
  );

  // Build filter variables for GraphQL query
  const filterVariables: PolicyChecksFilterVariables = useMemo(
    () => ({
      ...(riskRatingFilters?.length && { rating: riskRatingFilters }),
      // TODO: Enable when backend is ready
      // ...(statusFilter && { state: statusFilter }),
      ...(searchFilters?.length && { queryTerms: searchFilters }),
    }),
    [riskRatingFilters, statusFilter, searchFilters],
  );

  return {
    filterVariables,
    filterForm,
    activeFilterCount: activeFilters.length,
    resetFilters,
  };
}
